// src/ui/screens/post/SearchScreen.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../../../firebase';
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  startAt,
  endAt,
  where,
} from 'firebase/firestore';
import { ListingItem } from './widgets/ListingItem';
import { UserItem } from '../../../components/UserItem';
import { FilterIconButton, FilterState } from './widgets/FilterSheet';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  cardLight: '#2C2F36',
};

const MAX_HISTORY = 10;
const HISTORY_KEY = 'search_history_v1';

// ── History Row ─────────────────────────────────────────────────────────────

const HistoryRow = ({ query, onTap, onRemove }) => {
  return (
    <button
      onClick={onTap}
      className="flex items-center w-full py-2.5 hover:bg-filatom-card-hover transition-colors rounded-lg px-2"
    >
      {/* FIX: `w-4.5 h-4.5` isn't a real Tailwind utility (the default
          spacing scale has no 4.5 step), so it was silently dropped,
          leaving this <svg> with no explicit size and rendering at its
          oversized intrinsic default — that's the "big and weird" icon
          symptom. Swapped for an arbitrary-value class sized to match the
          Flutter icon size (18px). */}
      <svg className="w-[18px] h-[18px] text-white/30 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span className="text-white/75 text-sm font-medium ml-3 flex-1 truncate text-left">
        {query}
      </span>
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="p-1 text-white/30 hover:text-white/60"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </button>
  );
};

// ── SearchScreen ─────────────────────────────────────────────────────────────

export const SearchScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialFilter = location.state?.initialFilter || null;

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [history, setHistory] = useState([]);
  const [filterState, setFilterState] = useState(
    initialFilter ? new FilterState(initialFilter) : new FilterState()
  );
  const [currentTab, setCurrentTab] = useState(0);

  // Listings state
  const [trendingListings, setTrendingListings] = useState([]);
  const [loadingTrending, setLoadingTrending] = useState(true);
  const [searchedListings, setSearchedListings] = useState([]);
  const [loadingSearchListings, setLoadingSearchListings] = useState(false);

  // Users state
  const [allUsers, setAllUsers] = useState([]);
  const [loadingAllUsers, setLoadingAllUsers] = useState(true);
  const [searchedUsers, setSearchedUsers] = useState([]);
  const [loadingSearchUsers, setLoadingSearchUsers] = useState(false);

  const debounceRef = useRef(null);
  const searchInputRef = useRef(null);

  // ── Load history ──────────────────────────────────────────────────────────

  useEffect(() => {
    const loadHistory = () => {
      try {
        const stored = localStorage.getItem(HISTORY_KEY);
        if (stored) {
          setHistory(JSON.parse(stored));
        }
      } catch (error) {
        console.error('Load history error:', error);
      }
    };
    loadHistory();
  }, []);

  // ── Save history ──────────────────────────────────────────────────────────

  const saveHistory = (newHistory) => {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));
    } catch (error) {
      console.error('Save history error:', error);
    }
  };

  const addToHistory = (query) => {
    if (!query.trim()) return;
    const updated = [
      query.trim(),
      ...history.filter(h => h.toLowerCase() !== query.trim().toLowerCase()),
    ].slice(0, MAX_HISTORY);
    setHistory(updated);
    saveHistory(updated);
  };

  const removeFromHistory = (query) => {
    const updated = history.filter(h => h !== query);
    setHistory(updated);
    saveHistory(updated);
  };

  const clearAllHistory = () => {
    setHistory([]);
    saveHistory([]);
  };

  // ── Load trending listings ────────────────────────────────────────────────

  useEffect(() => {
    const loadTrending = async () => {
      setLoadingTrending(true);
      try {
        const q = query(
          collection(db, 'listings'),
          orderBy('viewCount', 'desc'),
          limit(40)
        );
        const snap = await getDocs(q);
        const listings = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTrendingListings(listings);
      } catch (error) {
        console.error('Trending listings error:', error);
      } finally {
        setLoadingTrending(false);
      }
    };
    loadTrending();
  }, []);

  // ── Load all users ────────────────────────────────────────────────────────

  useEffect(() => {
    const loadUsers = async () => {
      setLoadingAllUsers(true);
      try {
        const q = query(
          collection(db, 'users'),
          limit(80)
        );
        const snap = await getDocs(q);
        const users = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Shuffle users
        const shuffled = users.sort(() => Math.random() - 0.5);
        setAllUsers(shuffled);
      } catch (error) {
        console.error('Load users error:', error);
      } finally {
        setLoadingAllUsers(false);
      }
    };
    loadUsers();
  }, []);

  // ── Search listings ──────────────────────────────────────────────────────

  const searchListings = useCallback(async (queryText) => {
    if (!queryText.trim()) {
      setSearchedListings([]);
      return;
    }

    setLoadingSearchListings(true);
    setIsSearching(true);

    try {
      const lower = queryText.toLowerCase();
      const q = query(
        collection(db, 'listings'),
        orderBy('viewCount', 'desc'),
        limit(200)
      );
      const snap = await getDocs(q);

      const results = snap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(item => {
          const title = (item.title || '').toLowerCase();
          const desc = (item.description || '').toLowerCase();
          return title.includes(lower) || desc.includes(lower);
        });

      // Apply filters
      const filtered = filterState.isEmpty
        ? results
        : results.filter(item => filterState.matches(item));

      setSearchedListings(filtered);
    } catch (error) {
      console.error('Search listings error:', error);
    } finally {
      setLoadingSearchListings(false);
      setIsSearching(false);
    }
  }, [filterState]);

  // ── Search users ──────────────────────────────────────────────────────────

  const searchUsers = useCallback(async (queryText) => {
    if (!queryText.trim()) {
      setSearchedUsers([]);
      return;
    }

    setLoadingSearchUsers(true);

    try {
      const lower = queryText.toLowerCase();

      // Search by displayName
      const nameQuery = query(
        collection(db, 'users'),
        orderBy('displayNameLower'),
        startAt([lower]),
        endAt([lower + '\uf8ff']),
        limit(30)
      );
      const nameSnap = await getDocs(nameQuery);

      // Search by username
      const userQuery = query(
        collection(db, 'users'),
        orderBy('username'),
        startAt([lower]),
        endAt([lower + '\uf8ff']),
        limit(30)
      );
      const userSnap = await getDocs(userQuery);

      const seen = new Set();
      const results = [];

      for (const doc of [...nameSnap.docs, ...userSnap.docs]) {
        if (seen.has(doc.id)) continue;
        seen.add(doc.id);
        results.push({ id: doc.id, ...doc.data() });
      }

      setSearchedUsers(results);
    } catch (error) {
      console.error('Search users error:', error);
    } finally {
      setLoadingSearchUsers(false);
    }
  }, []);

  // ── Handle search input ──────────────────────────────────────────────────

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const trimmed = value.trim();
    if (!trimmed) {
      setSearchedListings([]);
      setSearchedUsers([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    debounceRef.current = setTimeout(() => {
      addToHistory(trimmed);
      searchListings(trimmed);
      searchUsers(trimmed);
    }, 450);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    const trimmed = searchQuery.trim();
    if (trimmed) {
      addToHistory(trimmed);
      searchListings(trimmed);
      searchUsers(trimmed);
    }
  };

  const handleApplyQuery = (queryText) => {
    setSearchQuery(queryText);
    addToHistory(queryText);
    searchListings(queryText);
    searchUsers(queryText);
    searchInputRef.current?.focus();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchedListings([]);
    setSearchedUsers([]);
    setIsSearching(false);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    searchInputRef.current?.focus();
  };

  const handleFilterApply = (result) => {
    setFilterState(result);
    if (searchQuery.trim()) {
      searchListings(searchQuery);
    }
  };

  // ── Format price for filter display ──────────────────────────────────────

  const formatPrice = (p) => {
    if (p >= 1000000000) return `${(p / 1000000000).toFixed(1)}B`;
    if (p >= 1000000) return `${(p / 1000000).toFixed(0)}M`;
    if (p >= 1000) return `${(p / 1000).toFixed(0)}K`;
    return String(Math.round(p));
  };

  const formatPriceRange = () => {
    const hasMin = filterState.minPrice !== null;
    const hasMax = filterState.maxPrice !== null;
    if (hasMin && hasMax) {
      return `${formatPrice(filterState.minPrice)} - ${formatPrice(filterState.maxPrice)}`;
    }
    if (hasMin) return `From ${formatPrice(filterState.minPrice)}`;
    return `Up to ${formatPrice(filterState.maxPrice)}`;
  };

  const activeFilterCount = filterState.activeCount;

  // ── Render filter chips ──────────────────────────────────────────────────

  const renderFilterChips = () => {
    if (activeFilterCount === 0) return null;

    const chips = [];

    if (filterState.district) {
      chips.push({
        label: filterState.district,
        onRemove: () => {
          setFilterState(filterState.copyWith({ district: null, division: null }));
          if (searchQuery.trim()) searchListings(searchQuery);
        },
      });
    }
    if (filterState.division) {
      chips.push({
        label: filterState.division,
        onRemove: () => {
          setFilterState(filterState.copyWith({ division: null }));
          if (searchQuery.trim()) searchListings(searchQuery);
        },
      });
    }
    if (filterState.category) {
      chips.push({
        label: filterState.category,
        onRemove: () => {
          setFilterState(filterState.copyWith({ category: null, subCategory: null }));
          if (searchQuery.trim()) searchListings(searchQuery);
        },
      });
    }
    if (filterState.subCategory) {
      chips.push({
        label: filterState.subCategory,
        onRemove: () => {
          setFilterState(filterState.copyWith({ subCategory: null }));
          if (searchQuery.trim()) searchListings(searchQuery);
        },
      });
    }
    if (filterState.minPrice !== null || filterState.maxPrice !== null) {
      chips.push({
        label: formatPriceRange(),
        onRemove: () => {
          setFilterState(filterState.copyWith({ minPrice: null, maxPrice: null }));
          if (searchQuery.trim()) searchListings(searchQuery);
        },
      });
    }

    return (
      <div className="flex flex-wrap gap-1.5 px-3.5 md:px-0 pb-2">
        {chips.map((chip, index) => (
          <div
            key={index}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border"
            style={{
              backgroundColor: COLORS.card,
              borderColor: 'rgba(255,255,255,0.1)',
            }}
          >
            <svg className="w-3 h-3 text-filatom-primary/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span className="text-white/80 text-[11px] font-medium">{chip.label}</span>
            <button
              onClick={chip.onRemove}
              className="text-white/50 hover:text-white/80"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
        <button
          onClick={() => {
            setFilterState(new FilterState());
            if (searchQuery.trim()) searchListings(searchQuery);
          }}
          className="px-2.5 py-1 rounded-full text-[11px] font-medium border"
          style={{
            backgroundColor: `${COLORS.orange}1F`,
            borderColor: `${COLORS.orange}80`,
            color: COLORS.orange,
          }}
        >
          Clear all
        </button>
      </div>
    );
  };

  // ── Render listings grid ─────────────────────────────────────────────────

  const renderListingsGrid = (listings, header) => {
    if (listings.length === 0 && activeFilterCount > 0) {
      return (
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="flex flex-col items-center gap-4">
            <svg className="w-16 h-16 text-white/12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <p className="text-white/35 text-center text-sm font-medium">No listings match your filters</p>
            <button
              onClick={() => {
                setFilterState(new FilterState());
                if (searchQuery.trim()) searchListings(searchQuery);
              }}
              className="px-4 py-2 rounded-full text-xs font-semibold border"
              style={{
                backgroundColor: `${COLORS.orange}1F`,
                borderColor: `${COLORS.orange}66`,
                color: COLORS.orange,
              }}
            >
              Clear all filters
            </button>
          </div>
        </div>
      );
    }

    if (listings.length === 0) {
      return (
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="flex flex-col items-center gap-2">
            <svg className="w-14 h-14 text-white/10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <p className="text-white/40 text-center text-sm font-medium">No results found</p>
            <p className="text-white/20 text-xs">Try different keywords or adjust your filters</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 overflow-y-auto pb-32">
        {header}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 md:gap-4 px-2.5 md:px-0">
          {listings.map((listing) => (
            <ListingItem key={listing.id} listing={listing} />
          ))}
        </div>
      </div>
    );
  };

  // ── Render users list ────────────────────────────────────────────────────

  const renderUsersList = (users, header) => {
    if (users.length === 0) {
      return (
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="flex flex-col items-center gap-2">
            <svg className="w-14 h-14 text-white/10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className="text-white/40 text-center text-sm font-medium">No users found</p>
            <p className="text-white/20 text-xs">Try a different name or username</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 overflow-y-auto pb-32">
        {header}
        {/* Desktop: the stacked mobile list becomes a multi-column grid of
            user cards (md:grid only activates ≥768px — below that this is
            still the plain stacked `px-2` div it always was). */}
        <div className="px-2 md:px-0 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-3">
          {users.map((user) => (
            <UserItem key={user.id} user={user} />
          ))}
        </div>
      </div>
    );
  };

  // ── Render posted tab ────────────────────────────────────────────────────

  const renderPostedTab = () => {
    if (searchQuery.trim()) {
      if (loadingSearchListings || isSearching) {
        return (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
          </div>
        );
      }
      return renderListingsGrid(
        searchedListings,
        <div className="flex items-center gap-2 px-3.5 md:px-0 py-3">
          <svg className="w-[18px] h-[18px] text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="text-white text-sm font-bold">Results for "{searchQuery}"</span>
        </div>
      );
    }

    if (loadingTrending) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    const filteredTrending = filterState.isEmpty
      ? trendingListings
      : trendingListings.filter(item => filterState.matches(item));

    return renderListingsGrid(
      filteredTrending,
      <>
        {/* History */}
        {history.length > 0 && (
          <div className="px-3.5 md:px-0 py-3">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-white text-sm font-bold">Recent</span>
              <button
                onClick={clearAllHistory}
                className="text-white/40 text-xs"
              >
                Clear all
              </button>
            </div>
            <div className="space-y-0.5 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-x-4 md:space-y-0">
              {history.map((h) => (
                <HistoryRow
                  key={h}
                  query={h}
                  onTap={() => handleApplyQuery(h)}
                  onRemove={() => removeFromHistory(h)}
                />
              ))}
            </div>
            <div className="h-px bg-white/5 my-3" />
          </div>
        )}

        {/* Trending header */}
        {/* FIX: same invalid `w-4.5 h-4.5` class as HistoryRow above — this
            was the "trending icon appearing so big and weird" from the
            screenshot. */}
        <div className="flex items-center gap-2 px-3.5 md:px-0 py-2">
          <svg className="w-[18px] h-[18px] text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.5-7S13 7 14 9c1-2 2.5-4 2.5-4a8 8 0 011.157 13.657z" />
          </svg>
          <span className="text-white text-sm font-bold">
            {filterState.isEmpty ? 'Trending' : 'Trending • Filtered'}
          </span>
        </div>

        {/* Filter chips */}
        {renderFilterChips()}
      </>
    );
  };

  // ── Render users tab ─────────────────────────────────────────────────────

  const renderUsersTab = () => {
    if (searchQuery.trim()) {
      if (loadingSearchUsers) {
        return (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
          </div>
        );
      }
      return renderUsersList(
        searchedUsers,
        <div className="flex items-center gap-2 px-3.5 md:px-0 py-3">
          <svg className="w-[18px] h-[18px] text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="text-white text-sm font-bold">Users for "{searchQuery}"</span>
        </div>
      );
    }

    if (loadingAllUsers) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    return renderUsersList(
      allUsers,
      // FIX: this is the "people icon appearing so big and weird" —
      // same invalid `w-4.5 h-4.5` class.
      <div className="flex items-center gap-2 px-3.5 md:px-0 py-3">
        <svg className="w-[18px] h-[18px] text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
        <span className="text-white text-sm font-bold">People you may know</span>
      </div>
    );
  };

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* Top Bar
          Desktop: the bar's contents center to the same max-w-6xl column
          as the rest of the page and gain a hairline bottom border and
          a taller search field, so it reads as a proper search header
          instead of a mobile bar stretched full-width. */}
      <div className="pt-safe-top px-3 pb-2.5 bg-filatom-bg md:px-10 md:pt-6 md:pb-4 md:border-b md:border-white/5">
        <div className="flex items-center gap-2.5 md:max-w-6xl md:mx-auto md:gap-4">
          {/* FIX: `w-9.5 h-9.5` isn't a valid Tailwind class either (no 9.5
              step in the default scale) — this button had no explicit size
              and the arrow icon inside it (also `w-4.5 h-4.5`) rendered
              oversized for the same reason as the icons above. */}
          <button
            onClick={() => navigate(-1)}
            className="w-[38px] h-[38px] rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: COLORS.card }}
          >
            <svg className="w-[18px] h-[18px] text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div
            className="flex-1 h-11 md:h-12 rounded-xl flex items-center gap-2 px-3 md:px-4"
            style={{
              backgroundColor: COLORS.card,
              border: activeFilterCount > 0 ? `1px solid ${COLORS.orange}80` : 'none',
            }}
          >
            <svg className="w-5 h-5 text-white/35" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>

            <form onSubmit={handleSearchSubmit} className="flex-1">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search listings, users…"
                className="w-full bg-transparent text-white text-sm outline-none placeholder:text-white/25"
                autoFocus
              />
            </form>

            {/* FIX: this used to be a hand-rolled button that called
                navigate('/filter') — a route that was never registered in
                App.js, so tapping it just landed on a blank/plain page.
                FilterIconButton (already imported above, and used the same
                way in PostScreen.jsx) manages its own popup state and
                renders the actual FilterSheet — swapping to it makes the
                filter icon behave the same way it does everywhere else in
                the app. */}
            <FilterIconButton current={filterState} onApply={handleFilterApply} size={20} />

            {(searchQuery.trim() || isSearching) && (
              <button
                onClick={handleClearSearch}
                className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
              >
                <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab Bar
          Desktop: same centered column, tabs switch from equal-width
          full-bleed to left-aligned with generous padding (matching the
          tab-bar treatment used on the profile screens). */}
      <div className="bg-filatom-bg">
        <div className="flex border-b border-white/5 md:max-w-6xl md:mx-auto md:px-10">
          <button
            onClick={() => setCurrentTab(0)}
            className={`flex-1 md:flex-none py-3 md:py-4 md:px-8 text-sm font-medium transition-colors relative ${
              currentTab === 0 ? 'text-white' : 'text-white/35'
            }`}
          >
            Posted
            {currentTab === 0 && (
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full" style={{ backgroundColor: COLORS.orange }} />
            )}
          </button>
          <button
            onClick={() => setCurrentTab(1)}
            className={`flex-1 md:flex-none py-3 md:py-4 md:px-8 text-sm font-medium transition-colors relative ${
              currentTab === 1 ? 'text-white' : 'text-white/35'
            }`}
          >
            Users
            {currentTab === 1 && (
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full" style={{ backgroundColor: COLORS.orange }} />
            )}
          </button>
        </div>
      </div>

      {/* Tab Content
          Desktop: contained to the same max-w-6xl column, with listings/
          users grids widening via the responsive grid classes above. */}
      <div className="flex-1 h-[calc(100vh-130px)] md:h-[calc(100vh-170px)] md:max-w-6xl md:mx-auto md:px-10">
        {currentTab === 0 ? renderPostedTab() : renderUsersTab()}
      </div>
    </div>
  );
};

export default SearchScreen;